import { z } from 'zod';
import { maKySchema } from './ky.js';
import { granularitySchema } from './sync.js';
/* ------------------------------------------------------------------ */
/* Truy vấn chung                                                      */
/* ------------------------------------------------------------------ */
/**
 * Bộ lọc dùng chung cho mọi endpoint của module analytics.
 * `channelIds` và `groupId` loại trừ nhau; không truyền gì là toàn bộ kênh
 * người dùng được xem.
 */
/**
 * Mốc dữ liệu: ngày gần nhất **đã có số liệu**.
 *
 * Giao diện dựng khoảng của kỳ từ mốc này (`khoangTuNgayCuoi`) thay vì lùi từ
 * "hôm qua", để khung "28 ngày qua" trùng với YouTube Studio.
 */
export const mocSoLieuSchema = z.object({
    /** `null` khi chưa đồng bộ kênh nào; lúc đó giao diện lùi về hôm qua. */
    ngayCuoiCoSoLieu: z.string().nullable(),
    /**
     * Ngày **cũ nhất** có số liệu, cho kỳ "Toàn thời gian".
     *
     * `null` khi chưa đồng bộ kênh nào.
     */
    ngaySomNhatCoSoLieu: z.string().nullable(),
    /**
     * Các năm **có số liệu thật** trong phạm vi đang xem, mới nhất trước.
     *
     * Ô chọn kỳ chỉ bày đúng những năm này. Suy từ hai mốc trên là không đủ:
     * năm trống nằm giữa hai đầu vẫn phải ẩn (xem `cacNamCoSoLieu`).
     */
    cacNam: z.array(z.string()),
});
/**
 * Tham số của `GET /analytics/moc`.
 *
 * `channelId` thu phạm vi về **một** kênh: trang chi tiết kênh và trang Hiệu
 * suất khi đang lọc một kênh phải lấy mốc của đúng kênh đó, nếu không ô chọn
 * kỳ bày những năm của kênh khác. Không truyền thì mốc tính trên toàn bộ phạm
 * vi dữ liệu của người đăng nhập.
 */
export const mocSoLieuQuerySchema = z.object({
    channelId: z.string().min(1).optional(),
});
export const analyticsRangeQuerySchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /**
     * **Mã kỳ** của khoảng (`28d`, `2026-10`, `tu-…_den-…`) — không bắt buộc.
     *
     * `from`/`to` một mình không nói được "kỳ trước" là gì: 01–26/10 có thể là
     * tháng 10 đang chạy (so với 01–26/09) hay một khoảng tự chọn (so với 26 ngày
     * liền trước). Thiếu mã thì máy chủ vẫn lùi 26 ngày liền trước như cũ.
     */
    ky: maKySchema.optional(),
    /// Danh sách id kênh, phân cách bằng dấu phẩy trên query string.
    channelIds: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v)),
    groupId: z.string().optional(),
});
/** Một chỉ số kèm so sánh với kỳ trước cùng độ dài. */
export const metricWithDeltaSchema = z.object({
    value: z.number(),
    previous: z.number(),
    deltaPercent: z.number().nullable(),
});
/**
 * Chỉ số có thể **chưa đủ số liệu để tính**.
 *
 * `chuaDuSoLieu = true` thì giao diện phải hiện `—`, không hiện `value` (lúc đó
 * `value` là 0 chỉ vì phép chia không có mẫu số). Dùng cho RPM và lượt xem:
 * ngày chỉ có doanh thu mà thiếu lượt xem từng cho RPM 621,81 $.
 */
export const metricCoTheThieuSchema = metricWithDeltaSchema.extend({
    chuaDuSoLieu: z.boolean(),
    /// Số ngày trong kỳ thật sự vào được phép tính.
    soNgayDuSoLieu: z.number().int().nonnegative(),
});
/**
 * Chỉ số tiền có thể **chưa có số**.
 *
 * Khác {@link metricCoTheThieuSchema} ở chỗ không có `soNgayDuSoLieu`: doanh
 * thu khả dụng đếm theo **ngày có doanh thu**, mà con số đó phải lấy thêm một
 * truy vấn chỉ để in một dòng chú thích. `chuaDuSoLieu` là đủ để giao diện
 * chọn giữa `—` và `0,00 $`.
 */
export const metricTienSchema = metricWithDeltaSchema.extend({
    chuaDuSoLieu: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Tổng quan                                                           */
/* ------------------------------------------------------------------ */
export const analyticsOverviewSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Khoảng của **kỳ trước** — ô "so kỳ trước" phải nói được mình so với ngày nào. */
    prevFrom: z.string(),
    prevTo: z.string(),
    /**
     * CTR trung bình có trọng số theo impressions, đơn vị phần trăm.
     *
     * `chuaDuSoLieu` khi không ngày nào trong kỳ có lượt hiển thị: Reporting API
     * chỉ có số từ ngày app tạo reporting job, và trước 20/10/2026 mỗi lượt
     * analytics còn đè số đã có về 0. Lúc đó giao diện hiện `—`, không `0 %`.
     */
    ctr: metricCoTheThieuSchema,
    /// Thời lượng xem trung bình, đơn vị giây.
    averageViewDuration: metricWithDeltaSchema,
    /// Tổng giờ xem.
    watchHours: metricWithDeltaSchema,
    /**
     * RPM trung bình, chỉ tính **những ngày có cả doanh thu và lượt xem > 0**
     * của các kênh có doanh thu. `chuaDuSoLieu` ⇒ giao diện hiện `—`.
     *
     * **Tuỳ chọn** vì đây là một chỉ số tiền: thiếu `revenue:view` thì máy chủ
     * **bỏ hẳn trường** khỏi đáp án (xem {@link boChiSoTien}). Bỏ hẳn, không gửi
     * `null` — một khoá tên `rpm` với giá trị `null` vẫn nói ra "chỗ này có một
     * con số tiền", và giao diện sẽ vẽ một ô `—` mà người xem không được phép
     * biết là có.
     */
    rpm: metricCoTheThieuSchema.optional(),
    /// Lượt xem; `chuaDuSoLieu` khi không ngày nào trong kỳ có số liệu lượt xem.
    views: metricCoTheThieuSchema,
    subscribersNet: metricWithDeltaSchema,
    /**
     * Lượt hiển thị (`impressions` của Reporting API).
     *
     * `chuaDuSoLieu` khi không ngày nào trong kỳ có số: Reporting API chỉ có dữ
     * liệu từ lúc app tạo reporting job cho kênh, nên `0` ở đây đọc ra "thumbnail
     * không hiện với ai" trong khi thật ra app chưa biết gì.
     */
    impressions: metricCoTheThieuSchema,
    /**
     * Doanh thu **gộp** của kỳ — đúng con số `gross` mà module `revenue` dùng
     * (tổng `estimatedRevenue` theo ngày, qua `sync.getRevenue`), nên ô này bằng
     * cột doanh thu gộp của trang Doanh thu và của bảng Quản lý kênh.
     *
     * `chuaDuSoLieu` khi không kênh nào trong phạm vi có doanh thu khả dụng.
     *
     * **Tuỳ chọn** — xem ghi chú ở `rpm`.
     */
    grossRevenue: metricTienSchema.optional(),
    currency: z.string(),
    /// `true` khi khoảng thời gian có ngày chưa chốt (dưới 3 ngày).
    provisional: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Cổng quyền doanh thu — kiểm theo ĐÁP ÁN, không theo tham số          */
/* ------------------------------------------------------------------ */
/**
 * Định dạng số nào **là tiền**.
 *
 * Rà soát bảo mật 31/10/2026, mục **S5**. Trước đợt 7 cổng quyền doanh thu áp
 * theo **tham số** `?chi-so=`: `/so-sanh-kenh`, `/chuoi-chi-so` và
 * `/doanh-thu` trả 403 đúng, nhưng `/overview` và `/chi-so` **không có** tham
 * số đó nên không qua cổng — và đáp án của chúng luôn chở `grossRevenue` và
 * `rpm`. Tài khoản vai trò "Biên tập / Dựng" (`analytics: assigned`,
 * `revenue: none` trong ma trận mặc định) đọc được doanh thu của cả phạm vi.
 *
 * Luật mới: cổng áp theo **trường trong đáp án**. Một đáp án có số tiền thì
 * phải qua cổng, bất kể route nhận tham số gì.
 */
export const DINH_DANG_TIEN = ['tien', 'rpm'];
export function laDinhDangTien(dinhDang) {
    return DINH_DANG_TIEN.includes(dinhDang);
}
/**
 * Bỏ các **thẻ chỉ số tiền** khỏi một dải thẻ khi người xem không có
 * `revenue:view`.
 *
 * Nhận bất cứ thứ gì có `dinhDang` — `TheChiSo` của bốn tab một kênh và
 * `OTomTat` của ba tab gộp nhiều kênh dùng hai enum khác nhau nhưng cùng tên
 * giá trị, nên một hàm phủ cả hai.
 */
export function locTheTien(the, duocXemTien) {
    return duocXemTien ? [...the] : the.filter((t) => !laDinhDangTien(t.dinhDang));
}
/**
 * Bỏ **hẳn** hai trường tiền khỏi đáp án tổng quan khi thiếu `revenue:view`.
 *
 * `delete` chứ không đặt `null`: khoá còn tên là còn nói ra "ở đây có một con
 * số tiền", và `metricTienSchema` không nhận `null` nên đáp án sẽ lệch hợp
 * đồng. `currency` giữ lại — nó là hằng `'USD'`, không phải số liệu.
 */
export function boChiSoTien(ov, duocXemTien) {
    if (duocXemTien)
        return ov;
    const { grossRevenue: _bo1, rpm: _bo2, ...conLai } = ov;
    return conLai;
}
export const watchTimeByChannelSchema = z.object({
    items: z.array(z.object({
        channelId: z.string(),
        channelName: z.string(),
        channelColor: z.string(),
        watchHours: z.number(),
        views: z.number().int().nonnegative(),
        percent: z.number(),
    })),
    totalWatchHours: z.number(),
    provisional: z.boolean(),
});
export const trafficSourcesResponseSchema = z.object({
    items: z.array(z.object({
        sourceType: z.string(),
        label: z.string(),
        views: z.number().int().nonnegative(),
        percent: z.number(),
    })),
    totalViews: z.number().int().nonnegative(),
    provisional: z.boolean(),
});
/* Nhãn nguồn lưu lượng: xem `nguonLuuLuong.ts` (`nhanNguonLuuLuong`). */
export const topVideosQuerySchema = analyticsRangeQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const topVideoSchema = z.object({
    videoId: z.string(),
    youtubeVideoId: z.string(),
    title: z.string(),
    thumbnailUrl: z.string().nullable(),
    channelId: z.string(),
    channelName: z.string(),
    publishedAt: z.iso.datetime().nullable(),
    views: z.number().int().nonnegative(),
    /// `null` = video chưa có số lượt hiển thị nào ⇒ giao diện hiện `—`.
    ctr: z.number().nullable(),
    averageViewDuration: z.number().int().nonnegative(),
    rpm: z.string().nullable(),
    currency: z.string(),
    /// Nhạc đã dùng, lấy từ TrackUsage của module music.
    tracks: z.array(z.object({ trackId: z.string(), title: z.string(), artist: z.string() })),
});
export const topVideosResponseSchema = z.object({
    items: z.array(topVideoSchema),
    provisional: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* Chuỗi thời gian                                                     */
/* ------------------------------------------------------------------ */
export const TIMESERIES_METRICS = ['views', 'watchTime', 'subscribers'];
export const timeseriesMetricSchema = z.enum(TIMESERIES_METRICS);
export const TIMESERIES_METRIC_LABEL = {
    views: 'Lượt xem',
    watchTime: 'Giờ xem',
    subscribers: 'Subscribers',
};
export const timeseriesQuerySchema = analyticsRangeQuerySchema.extend({
    metric: timeseriesMetricSchema.default('views'),
    granularity: granularitySchema.default('day'),
});
export const timeseriesResponseSchema = z.object({
    metric: timeseriesMetricSchema,
    granularity: granularitySchema,
    points: z.array(z.object({
        /// YYYY-MM-DD (day), YYYY-Www (week) hoặc YYYY-MM (month).
        date: z.string(),
        value: z.number(),
        provisional: z.boolean(),
    })),
});
/* ------------------------------------------------------------------ */
/* Tám chỉ số của trang Hiệu suất                                      */
/* ------------------------------------------------------------------ */
/**
 * Tám chỉ số bấm được của khung chỉ số trên `/phan-tich`.
 *
 * Mã là **chuỗi trong URL** (`?chi-so=ctr`), nên nó không được đổi: link đã
 * gửi cho nhau phải mở đúng chỉ số đó. Bốn mã đầu là số đếm, bốn mã sau là tỷ
 * lệ hoặc tiền.
 */
export const CHI_SO_HIEU_SUAT = [
    'luot-xem',
    'gio-xem',
    'subscribers',
    'luot-hien-thi',
    'ctr',
    'doanh-thu',
    'thoi-luong-xem',
    'rpm',
];
export const maChiSoSchema = z.enum(CHI_SO_HIEU_SUAT);
/** Cách giao diện định dạng con số của một chỉ số. */
export const DINH_DANG_CHI_SO = ['so', 'gio', 'tien', 'phan-tram', 'thoi-luong', 'rpm'];
export const dinhDangChiSoSchema = z.enum(DINH_DANG_CHI_SO);
/**
 * Cửa mà một kênh phải qua để được xếp hạng **tăng/giảm**.
 *
 * Phần trăm tăng trưởng trên mẫu số bé là một con số vô nghĩa mà trông như có
 * nghĩa — cùng hằng số và cùng lý do với `NGUONG_TANG_TRUONG_7_NGAY` của
 * `dashboard.ts`. Mỗi chỉ số canh cửa trên **khối lượng** của chính nó:
 *
 * - `luot-xem-truoc`: lượt xem **kỳ trước** ≥ ngưỡng. Đây là cửa mặc định, kể
 *   cả cho hai chỉ số trung bình (Thời lượng xem TB, RPM): mẫu số của chúng
 *   chính là lượt xem, nên kênh 200 lượt xem không được xếp hạng theo RPM.
 * - `luot-hien-thi-truoc`: lượt hiển thị **kỳ trước** ≥ ngưỡng, dùng cho chính
 *   chỉ số Lượt hiển thị.
 * - `luot-hien-thi-hai-ky`: lượt hiển thị của **cả hai** kỳ ≥ ngưỡng, dùng cho
 *   CTR. CTR một bên chỉ có 300 lượt hiển thị thì chênh "điểm %" là tiếng ồn.
 */
export const CUA_XEP_HANG = [
    'luot-xem-truoc',
    'luot-hien-thi-truoc',
    'luot-hien-thi-hai-ky',
];
export const cuaXepHangSchema = z.enum(CUA_XEP_HANG);
export const MO_TA_CHI_SO = {
    'luot-xem': {
        ma: 'luot-xem',
        nhan: 'Lượt xem',
        dinhDang: 'so',
        canQuyenDoanhThu: false,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
    'gio-xem': {
        ma: 'gio-xem',
        nhan: 'Giờ xem',
        dinhDang: 'gio',
        canQuyenDoanhThu: false,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
    subscribers: {
        ma: 'subscribers',
        nhan: 'Subscribers mới',
        dinhDang: 'so',
        canQuyenDoanhThu: false,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
    'luot-hien-thi': {
        ma: 'luot-hien-thi',
        nhan: 'Lượt hiển thị',
        dinhDang: 'so',
        canQuyenDoanhThu: false,
        soTheoDiem: false,
        cua: 'luot-hien-thi-truoc',
    },
    ctr: {
        ma: 'ctr',
        nhan: 'CTR',
        dinhDang: 'phan-tram',
        canQuyenDoanhThu: false,
        soTheoDiem: true,
        cua: 'luot-hien-thi-hai-ky',
    },
    'doanh-thu': {
        ma: 'doanh-thu',
        nhan: 'Doanh thu gộp',
        dinhDang: 'tien',
        canQuyenDoanhThu: true,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
    'thoi-luong-xem': {
        ma: 'thoi-luong-xem',
        nhan: 'Thời lượng xem TB',
        dinhDang: 'thoi-luong',
        canQuyenDoanhThu: false,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
    rpm: {
        ma: 'rpm',
        nhan: 'RPM',
        dinhDang: 'rpm',
        canQuyenDoanhThu: true,
        soTheoDiem: false,
        cua: 'luot-xem-truoc',
    },
};
/** Chỉ số nào cần `revenue:view` — một luật đọc ở cả hai phía. */
export function chiSoCanQuyenDoanhThu(ma) {
    return MO_TA_CHI_SO[ma].canQuyenDoanhThu;
}
/** Tám mã, bỏ hai mã tiền khi người xem không có `revenue:view`. */
export function chiSoXemDuoc(coQuyenDoanhThu) {
    return CHI_SO_HIEU_SUAT.filter((ma) => coQuyenDoanhThu || !chiSoCanQuyenDoanhThu(ma));
}
/* ------------------------------------------------------------------ */
/* Phạm vi của trang Hiệu suất                                         */
/* ------------------------------------------------------------------ */
/**
 * Giá trị của `?pham-vi=` — hàng chip phạm vi trên `/phan-tich`.
 *
 * Năm dạng: `tat-ca`, `toi-quan-ly`, `network:<id>`, `nhom:<id>`, `kenh:<id>`.
 * Mã lạ **không** rơi về `tat-ca` ở máy chủ: người bị giới hạn kênh mà gõ sai
 * mã phải nhận 400, chứ không phải âm thầm được mở rộng phạm vi.
 */
export const phamViHieuSuatSchema = z
    .string()
    .regex(/^(tat-ca|toi-quan-ly|(network|nhom|kenh):[A-Za-z0-9_-]{1,64})$/);
export const PHAM_VI_MAC_DINH = 'tat-ca';
/** `network:abc` → `{ loai: 'network', id: 'abc' }`. Mã lạ trả `null`. */
export function giaiMaPhamVi(ma) {
    if (ma === 'tat-ca')
        return { loai: 'tat-ca' };
    if (ma === 'toi-quan-ly')
        return { loai: 'toi-quan-ly' };
    const dau = ma.indexOf(':');
    if (dau < 0)
        return null;
    const loai = ma.slice(0, dau);
    const id = ma.slice(dau + 1);
    if (id === '')
        return null;
    if (loai === 'network' || loai === 'nhom' || loai === 'kenh')
        return { loai, id };
    return null;
}
/** Nhãn ngắn của phạm vi cho dòng phụ đầu trang; id cần tên thì tra ở giao diện. */
export const PHAM_VI_NHAN_CO_DINH = {
    'tat-ca': 'Tất cả kênh',
    'toi-quan-ly': 'Kênh tôi quản lý',
};
/* ------------------------------------------------------------------ */
/* Chuỗi theo ngày của một chỉ số                                      */
/* ------------------------------------------------------------------ */
/** Một điểm của biểu đồ đường. */
export const diemChuoiChiSoSchema = z.object({
    /// YYYY-MM-DD (day), YYYY-Www (week) hoặc YYYY-MM (month).
    date: z.string(),
    /**
     * `null` = **không có số liệu** khoảng đó.
     *
     * Biểu đồ để trống chỗ đó, không vẽ 0: một kênh chậm đồng bộ một ngày mà vẽ
     * thành 0 thì đường tụt xuống đáy rồi bật lên, trông như sự cố thật.
     */
    giaTri: z.number().nullable(),
    /// `false` khi YouTube chưa chốt số của khoảng này (ba ngày gần nhất).
    daChot: z.boolean(),
});
export const chuoiChiSoQuerySchema = z.object({
    ky: maKySchema.default('28d'),
    'pham-vi': phamViHieuSuatSchema.default(PHAM_VI_MAC_DINH),
    'chi-so': maChiSoSchema.default('luot-xem'),
});
export const chuoiChiSoSchema = z.object({
    chiSo: maChiSoSchema,
    dinhDang: dinhDangChiSoSchema,
    granularity: granularitySchema,
    from: z.string(),
    to: z.string(),
    prevFrom: z.string(),
    prevTo: z.string(),
    /** Kỳ này, cũ → mới. */
    diem: z.array(diemChuoiChiSoSchema),
    /**
     * Kỳ trước, cũ → mới, để đặt **chồng theo thứ tự** lên `diem`.
     *
     * Ghép theo **chỉ số thứ tự**, không theo khoá ngày (hai kỳ không cùng
     * ngày): điểm thứ ba của kỳ này nằm trên điểm thứ ba của kỳ trước. Kỳ trước
     * ngắn hơn (tháng 2 so với tháng 3) thì mảng này ngắn hơn và đoạn cuối không
     * có nét đứt — đúng hơn là kéo giãn cho bằng.
     */
    diemTruoc: z.array(diemChuoiChiSoSchema),
    currency: z.string(),
    provisional: z.boolean(),
});
/* ------------------------------------------------------------------ */
/* So sánh kênh                                                        */
/* ------------------------------------------------------------------ */
export const CACH_SAP_SO_SANH = ['tang', 'giam', 'cao'];
export const cachSapSoSanhSchema = z.enum(CACH_SAP_SO_SANH);
export const CACH_SAP_NHAN = {
    tang: 'Tăng mạnh nhất',
    giam: 'Giảm mạnh nhất',
    cao: 'Cao nhất',
};
/**
 * Số dòng khung so sánh bày trước khi bấm "Xem tất cả".
 *
 * 20, không phải 10: công ty có 16 kênh và bảng **liệt kê đủ** (kênh chưa đủ
 * điều kiện xuống cuối thay vì bị ẩn), nên 16 dòng vẫn đọc hết trong một lần
 * cuộn. Cắt ở 10 là bắt người ta bấm thêm một nút để thấy sáu kênh còn lại.
 */
export const SO_DONG_SO_SANH = 20;
/**
 * Cache của trang Hiệu suất, tính bằng giây.
 *
 * Ngắn hơn `DASHBOARD_CACHE_SECONDS` (300 s): người ta bấm qua tám ô chỉ số
 * trong vài chục giây để so, nên 120 s đủ che hết một lượt khảo sát, mà vẫn đủ
 * ngắn để nạp lại trang sau một lượt đồng bộ là thấy số mới.
 */
export const HIEU_SUAT_CACHE_SECONDS = 120;
export const soSanhKenhQuerySchema = z.object({
    ky: maKySchema.default('28d'),
    'pham-vi': phamViHieuSuatSchema.default(PHAM_VI_MAC_DINH),
    'chi-so': maChiSoSchema.default('luot-xem'),
    sap: cachSapSoSanhSchema.default('tang'),
    /** `SO_DONG_SO_SANH` cho khung gọn; "Xem tất cả" gửi mức cao hơn. */
    limit: z.coerce.number().int().min(1).max(500).default(SO_DONG_SO_SANH),
});
export const dongSoSanhKenhSchema = z.object({
    /** Hạng trong **toàn bộ** tập đủ điều kiện, không phải trong trang. */
    hang: z.number().int().positive(),
    channelId: z.string(),
    ten: z.string(),
    /** Tên Network đang trả tiền; `null` khi tiền đi AdSense trực tiếp. */
    network: z.string().nullable(),
    /** `null` = kỳ này chưa có số liệu ⇒ giao diện hiện `—`. */
    giaTri: z.number().nullable(),
    giaTriTruoc: z.number().nullable(),
    /**
     * Khoảng **thật sự** được cộng cho `giaTriTruoc`, sau phép cắt cho cùng số
     * ngày với kỳ này.
     *
     * Kỳ này có 27 trong 29 ngày thì kỳ trước chỉ cộng **27 ngày đầu**
     * (`01/09–27/09`), không phải cả `01/09–29/09` — nếu không thì phần trăm so
     * một kỳ 27 ngày với một kỳ 29 ngày và mọi kênh bị kéo xuống ~7 %. Bảng in
     * khoảng này ra: một cột "kỳ trước" không nói khoảng là một con số không ai
     * đối chiếu được với Studio.
     */
    khoangTruoc: z.object({ from: z.string(), to: z.string() }).nullable(),
    /**
     * Chênh tuyệt đối; với CTR là **điểm phần trăm**.
     *
     * Có ở **cả ba** chế độ, kể cả `cao` và kể cả dòng `chuaDuDieuKien`: phần
     * trăm là thứ người đọc dùng để hiểu con số kỳ này, nên bảng luôn in nó —
     * chip xám cho dòng chưa đủ điều kiện, nghĩa "để tham khảo". `null` chỉ khi
     * một trong hai kỳ chưa có số liệu.
     */
    delta: z.number().nullable(),
    /**
     * Phần trăm so kỳ trước. `null` ở hai chỗ **không chia được**: chỉ số so bằng
     * điểm (CTR), và kỳ trước bằng 0 hoặc chưa có số liệu.
     */
    pct: z.number().nullable(),
    /** Kỳ này hoặc kỳ trước còn ngày thiếu số liệu (dưới 90 % số ngày). */
    thieuDuLieu: z.boolean(),
    /**
     * `true` = phần trăm của kênh này **không có nghĩa**, nhưng kênh vẫn có mặt
     * trong bảng với giá trị kỳ này.
     *
     * Trước 31/10/2026 những kênh này bị **ẩn hẳn**, nên bảng của một kỳ đang
     * chạy chỉ còn 3 trong 16 kênh mà không màn nào nói 13 kênh kia đi đâu. Nay
     * chúng xuống cuối, dưới một vạch ngăn, và `lyDo` nói vì sao.
     *
     * Chỉ hai loại lý do: **thiếu ngày** (dưới 90 % ở một trong hai kỳ) và **dưới
     * ngưỡng khối lượng**. Kênh **giảm** ở chế độ `tang` không thuộc nhóm này —
     * nó so được, chỉ xếp cuối nhóm đủ điều kiện với chip ▼.
     *
     * Luôn `false` ở chế độ `cao` — chế độ đó không chia cho gì nên không có khái
     * niệm "đủ điều kiện".
     */
    chuaDuDieuKien: z.boolean(),
    /**
     * Lý do ngắn in **cạnh** chip phần trăm khi `chuaDuDieuKien`: `kỳ trước thiếu
     * 20 ngày`, `kỳ trước chỉ 1,9K`, `kênh mới, chưa có số liệu`. `null` khi kênh
     * đủ điều kiện.
     */
    lyDo: z.string().nullable(),
});
export const soSanhKenhSchema = z.object({
    chiSo: maChiSoSchema,
    dinhDang: dinhDangChiSoSchema,
    sap: cachSapSoSanhSchema,
    /** `true` khi chỉ số so bằng điểm phần trăm (CTR) — chip ghi "điểm". */
    soTheoDiem: z.boolean(),
    from: z.string(),
    to: z.string(),
    prevFrom: z.string(),
    prevTo: z.string(),
    /**
     * Tổng số **dòng của bảng**, để nút "Xem tất cả" nói còn bao nhiêu.
     *
     * Từ 31/10/2026 đây là **mọi kênh trong phạm vi**, không chỉ kênh đủ điều
     * kiện: bảng liệt kê đủ và kênh chưa đủ xuống cuối. Số kênh xếp hạng được thì
     * đếm bằng `items.filter((d) => !d.chuaDuDieuKien)`.
     */
    tong: z.number().int().nonnegative(),
    items: z.array(dongSoSanhKenhSchema),
    /** Ngưỡng đã lọc ở chế độ tăng/giảm; `null` ở chế độ `cao`. */
    nguong: z.number().int().nonnegative().nullable(),
    /** Cửa đã dùng, cho dòng chú thích dưới khung; `null` ở chế độ `cao`. */
    cua: cuaXepHangSchema.nullable(),
    currency: z.string(),
    provisional: z.boolean(),
});
//# sourceMappingURL=analytics.js.map